import { ProductImageCarousel } from '@/components/ProductImageCarousel/carousel';
import { Form } from '@/components/ui/form';
import { useUserContext } from '@/context/user';
import { useAddToCart } from '@/hooks/ProductFullDetail/hooks/use-add-to-cart';
import { useFinalPriceAndRegularPrice } from '@/hooks/ProductFullDetail/hooks/use-final-price-and-regular-price';
import { useGetIsOutOfStock } from '@/hooks/ProductFullDetail/hooks/use-get-is-out-of-stock';
import { useGetIsPreOrderInfo } from '@/hooks/ProductFullDetail/hooks/use-get-is-pre-order-info';
import { useGetOptionCode } from '@/hooks/ProductFullDetail/hooks/use-get-option-code';
import { useGetProductPrice } from '@/hooks/ProductFullDetail/hooks/use-get-product-price';
import { useToasts } from '@/hooks/Toasts/useToasts';
import { useGetMediaGalleryEntries } from '@/hooks/useGetMediaGalleryEntries';
import { useIsMobile } from '@/hooks/useIsMobile';
import { capitalizeFirstLetter, formatString } from '@/lib/format-string';
import { cn } from '@/lib/utils';
import { findMatchingVariant } from '@/util/findMatchingProductVariant';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dispatch, Fragment, SetStateAction, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Dialog from '../Dialog';
import Price from '../Price';
import { useGetCustmerDetail } from '../ReferAFriend/useGetCustmerDetail';
import { Icons } from '../ui/icons';
import ChooseOptionModal from './ChooseOptionModal';
import NotifyForm from './NotifyForm';
import { AddCartButton } from './add-cart-button';
// import { MaybeSignUpLink } from './maybe-signup-link';
import { MobileAddToCartSuccessTip } from './mobile-addtocart-success-tip';
// import { ProductDescription } from './product-description';
import { ProductFormError } from './product-form-error';
import { QuantityStepperNew } from './quantity-stepper';
// import { SezzlePay } from './sezzle-pay';
// import { Social } from './social';
import Link from 'next/link';
import { FindYourSize } from './find-your-size';
import { StandardOptions } from './standard-options';
import type { ConfigurableOption, ProductRoot, SelectedOptions, SelectedOptionsWithoutQuantity } from './types/product';
import { useOptionsSpecs } from './use-options-sepcs';

export const ProductFullDetailQuickView = ({
  product,
  defaultValues,
  handleColorLabelChange,
  seoColorLabel,
  productLink,
  setOpen,
  setQuickViewSuccessOpen,
  setQuickViewSuccessNumber,
}: {
  product: ProductRoot;
  handleColorLabelChange: any;
  defaultValues: any;
  seoColorLabel: string;
  productLink: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setQuickViewSuccessOpen: Dispatch<SetStateAction<boolean>>;
  setQuickViewSuccessNumber: Dispatch<SetStateAction<number>>;
}) => {
  const [, toastActions] = useToasts();
  const { addToast } = toastActions as any;

  const [{ isSignedIn: isUserSignedIn }] = useUserContext();
  const [notifyModalVisible, setNotifyModalVisible] = useState(false);
  const [chooseOptionsModalVisible, setChooseOptionsModalVisible] = useState(false);
  const { isMobile } = useIsMobile();

  const createFormSchema = (options: ConfigurableOption[]) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    options?.forEach(item => {
      schemaFields[item?.attribute_code] = z.object(
        {
          uid: z.string().min(1),
          label: z.string(),
          option_attribute_id: z.string(),
          option_selection_value: z.number().optional(),
        },
        {
          message: `This is a required field!`,
        },
      );
    });

    schemaFields['quantity'] = z.string().min(1, {
      message: `This is a required field!`,
    });

    return z.object(schemaFields);
  };

  const FormSchema = createFormSchema(product.configurable_options);

  const form = useForm({
    resolver: zodResolver(FormSchema as any),
    defaultValues: defaultValues,
  });

  const allCPData = form.watch() as SelectedOptions;

  const allCpDataWithoutQuantity = Object.fromEntries(
    Object.entries(allCPData).filter(([key]) => key !== 'quantity'),
  ) as SelectedOptionsWithoutQuantity;

  const addToCartQty = form.watch('quantity');
  const sizeChartInfo = product.product_size_chart as any;
  const hasSizeChart = Boolean(sizeChartInfo?.content);
  const hasConfigurableOptions = Array.isArray(product.configurable_options) && product.configurable_options.length > 0;

  const { optionCodes } = useGetOptionCode({
    product,
  });

  const { optionSpecs, handleClick } = useOptionsSpecs({
    product,
  });

  const optionSelections = useMemo(() => {
    const optionMap = new Map();
    const options = product?.configurable_options || [];

    options.forEach(item => {
      const { attribute_id } = item;

      optionMap.set(attribute_id, undefined);
    });

    // Iterate through each property in the JSON object
    for (const [key, value] of Object.entries(allCpDataWithoutQuantity)) {
      if (typeof value === 'object' && value !== null) {
        const item = allCpDataWithoutQuantity[key];

        optionMap.set(item.option_attribute_id, item.option_selection_value);
      }
    }

    return optionMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCpDataWithoutQuantity]);

  const selectedOptionsArray = useMemo(() => {
    return Object.values(allCpDataWithoutQuantity)
      .filter(item => item && item.uid)
      .map(item => item.uid);
  }, [allCpDataWithoutQuantity]);

  const { isPreOrderInfo } = useGetIsPreOrderInfo({
    product,
    optionCodes,
    optionSelections,
  });
  const { productPrice } = useGetProductPrice({
    product,
    optionCodes,
    optionSelections,
  });
  const { finalPriceAndRegularPrice } = useFinalPriceAndRegularPrice({
    product,
    optionCodes,
    optionSelections,
  });
  const { mediaGalleryEntries } = useGetMediaGalleryEntries({
    product,
    optionCodes,
    optionSelections,
  });
  const { isOutOfStock } = useGetIsOutOfStock({
    product,
    optionCodes,
    optionSelections,
  });
  const { errorMessage, handleAddToCart, isAdding, addToCartStatus, failedToCart } = useAddToCart({
    product,
    mediaGalleryEntries,
    selectedOptionsArray,
  });
  const item = findMatchingVariant({
    optionCodes,
    optionSelections,
    variants: product.variants,
  });

  const { isPreOrder, note, label } = isPreOrderInfo || {};
  const productDetails = {
    description: product.description,
    shortDescription: product.short_description,
    name: product.name,
    serial_name: product.serial_name,
    price: productPrice?.final_price,
    sku: product.sku,
    finalPriceAndRegularPrice: finalPriceAndRegularPrice,
  };

  const finalPrice = productDetails?.finalPriceAndRegularPrice?.finalPrice;

  const regularPrice = productDetails?.finalPriceAndRegularPrice?.regularPrice;

  const hasSpecialPrice = finalPrice && regularPrice && finalPrice < regularPrice;

  const { placeholderText } = useGetCustmerDetail();

  const onSubmit = async (values: any) => {
    if (product.configurable_options && selectedOptionsArray.length < product.configurable_options.length) {
      addToast({
        type: 'error',
        message: 'You need to choose options for your item.',
        timeout: 1000,
      });
      setChooseOptionsModalVisible(true);

      return;
    }

    if (isOutOfStock) {
      setNotifyModalVisible(true);

      return;
    }

    await handleAddToCart({
      quantity: values.quantity as number,
    });

    setQuickViewSuccessNumber(values.quantity);
    setQuickViewSuccessOpen(true);

    setTimeout(() => {
      setQuickViewSuccessOpen(false);
    }, 10000);

    setOpen(false);
  };

  const onNotifyFormClose = () => {
    setNotifyModalVisible(false);
  };

  const onChooseOptionModalClose = () => {
    setChooseOptionsModalVisible(false);
  };

  const socialMediaLinks = [
    {
      icon: (
        <Icons.InsIcon
          width={30}
          height={30}
        />
      ),
      href: 'https://www.instagram.com/direct/inbox/',
    },
    {
      icon: (
        <Icons.FacebookIcon
          width={30}
          height={30}
        />
      ),
      href: `https://www.facebook.com/sharer.php?u=${placeholderText}`,
    },
    {
      icon: (
        <Icons.EmailIcon
          width={30}
          height={30}
        />
      ),
      href: `mailto:?subject=HISEA Referral Program&body=Hi, I want to invite you to join HISEA family. Click this link to register: ${placeholderText}`,
    },
    {
      icon: (
        <Icons.XIcon
          width={30}
          height={30}
        />
      ),
      href: `https://twitter.com/share?url=${placeholderText}`,
    },
    {
      icon: (
        <Icons.PinIcon
          width={30}
          height={30}
        />
      ),
      href: `https://pinterest.com/pin/create/button/?url=${placeholderText}`,
    },
  ];

  const h1Title = seoColorLabel
    ? capitalizeFirstLetter(seoColorLabel) + ' ' + formatString(productDetails.name)
    : formatString(productDetails.name);

  return (
    <Fragment>
      <div className={cn('container mx-auto', isMobile ? 'h-[100vh] overflow-y-auto pb-[150px] pt-[50px]' : '')}>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn('pb-12 lg_pb-0', 'px-0', 'gap-x-lg')}
          >
            <div className=''>
              <section className={cn('mx-auto', 'my-0', 'relative', 'top-auto', 'w-full basis-full')}>
                <ProductImageCarousel
                  images={mediaGalleryEntries}
                  name={product.name}
                  sku={product.sku}
                  seoColorLabel={seoColorLabel}
                />
                {/* <Social productDetails={productDetails} /> */}
              </section>

              <div className={cn('mt-[20px]', isMobile ? 'px-[12px]' : '')}>
                <section className={cn('my-0', 'px-0')}>
                  <div className='border-b border-solid border-[#E0E0E0]'>
                    <h1
                      aria-live='polite'
                      className={cn(
                        'font-teko-medium text-[26px] font-normal leading-[37px] text-primary',
                        'items-center',
                      )}
                    >
                      {productDetails.serial_name || ''}
                    </h1>
                    <h3
                      aria-live='polite'
                      className={cn(
                        'mb-[10px] items-center  font-roboto-regular text-[18px] font-normal leading-[30px] text-primary',
                      )}
                    >
                      {h1Title}
                    </h3>
                    {/* <p
                    className={cn(
                      'text-right font-urw-din-medium text-sm text-primary sm_text-left sm_text-lg',
                      'uppercase',
                      'mb-[-25px] lg_mb-0',
                    )}
                  >
                    SKU: {productDetails.sku}
                  </p> */}

                    <div className='mb-[20px] mt-[10px] flex items-end justify-start gap-[5px]'>
                      <p className={cn('block font-roboto-medium text-[20px] font-medium leading-[28px] text-primary')}>
                        <Price
                          currencyCode={productDetails.price.currency}
                          value={finalPrice || productDetails.price.value}
                        />
                      </p>
                      <p className='inline-block font-roboto-regular text-[16px] font-normal leading-[24px] text-[#929292] line-through'>
                        {hasSpecialPrice && regularPrice && (
                          <Price
                            currencyCode={productDetails.price.currency}
                            value={regularPrice}
                          />
                        )}
                      </p>
                    </div>
                  </div>

                  {/* <SezzlePay
                    finalPrice={finalPrice}
                    productDetails={productDetails}
                  /> */}
                  {/* <MaybeSignUpLink /> */}

                  {/* {isUserSignedIn && (
                    <div className='my-[10px] flex items-center gap-[14px]  font-urw-din-medium text-sm font-[500] lg_text-[16px]'>
                      <span>Share this product</span>

                      <div className='flex justify-between gap-[15px] lg_justify-start'>
                        {socialMediaLinks.map((item, index) => (
                          <Link
                            key={index}
                            className='cursor-pointer'
                            href={item.href}
                            target='_blank'
                          >
                            {item.icon}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )} */}
                </section>

                <ProductFormError
                  errorMessage={errorMessage}
                  failedToCart={failedToCart}
                />

                <section className='mt-[20px]'>
                  <StandardOptions
                    configurableOptions={product.configurable_options}
                    variants={product.variants}
                    product={product}
                    classes={{
                      colorOptionImage: 'w-[47.5px] lg_w-[47.5px]',
                      colorOptionsWrapper: 'gap-x-[22px] lg_gap-x-[15px]',
                    }}
                    optionSpecs={optionSpecs}
                    handleClick={handleClick}
                    handleColorLabelChange={handleColorLabelChange}
                  />
                  {hasSizeChart ? (
                    <FindYourSize
                      className={cn('flex', hasConfigurableOptions && 'mt-[20px]')}
                      sizeChartInfo={sizeChartInfo}
                    />
                  ) : null}
                </section>
                <section className='mt-[20px]'>
                  <QuantityStepperNew
                    form={form}
                    className='hidden'
                  />
                </section>
                <section
                  className={cn(
                    isMobile
                      ? 'fixed bottom-0 left-0 w-full border-t border-solid border-[#E0E0E0] bg-white px-[12px] py-[20px] lg_px-0'
                      : '',
                  )}
                  style={{ boxShadow: isMobile ? '0px -4px 15px 0px #00000014' : '' }}
                >
                  <AddCartButton
                    isPreOrder={isPreOrder}
                    note={note}
                    isOutOfStock={isOutOfStock}
                    isAdding={isAdding}
                    label={label}
                    product={product}
                    item={item}
                    classes={{
                      btnClass: 'min-w-[300px] w-[100%] mx-auto h-[50px] text-[18px]',
                    }}
                  />
                  <Link
                    href={productLink}
                    className={cn(
                      'duration-300 mx-auto mt-[20px] block h-[50px] w-[100%] min-w-[300px] rounded-[25px] border-[1px] border-[#000] bg-white text-center font-roboto-medium text-[18px] leading-[50px] text-primary transition-all hover_bg-primary hover_text-white',
                    )}
                  >
                    <span>{'View Details'}</span>
                  </Link>
                </section>

                {/* <ProductDescription productDetails={productDetails} /> */}
              </div>
            </div>
          </form>
        </Form>
      </div>
      <Dialog
        classes={
          isMobile
            ? {
                container: '',
                dialog:
                  'dialog_dialog__2CiR7 h-full max-h-full w-[640px] bg-white grid grid-rows-autoFirst max-w-modal overflow-hidden pointer-events-auto shadow-dialog z-dialog lg_max-h-modal lg_max-w-full relative mx-auto mt-[20px]',
              }
            : {}
        }
        isOpen={notifyModalVisible}
        shouldShowButtons={false}
        shouldDisableHeader
        shouldDisableAllButtons
        width={isMobile ? '100%' : '42%'}
        isModal
        isForm={false}
      >
        <NotifyForm
          onClose={onNotifyFormClose}
          product_id={product.id}
          variants={product.variants}
          selectedOptionsArray={selectedOptionsArray}
          configurable_options={product.configurable_options}
          productDetailsName={productDetails.name}
        />
      </Dialog>

      {/* 选择商品的模态框 */}
      <Dialog
        classes={{
          dialog:
            'h-full max-h-full min-h-[217px] w-[640px] bg-white grid grid-rows-autoFirst max-w-modal overflow-hidden pointer-events-auto rounded-md shadow-dialog z-dialog lg_max-h-modal lg_max-w-full h-[217px] border-[#eff4f7] border-solid border-[15px]',
        }}
        isOpen={chooseOptionsModalVisible && !isMobile}
        shouldShowButtons={false}
        shouldDisableHeader
        shouldDisableAllButtons
        isModal
        isForm={false}
      >
        <ChooseOptionModal onClose={onChooseOptionModalClose} />
      </Dialog>

      <MobileAddToCartSuccessTip
        addToCartQty={addToCartQty}
        addToCartStatus={addToCartStatus}
      />
    </Fragment>
  );
};
